import gsap from "gsap";
import { SplitText } from "gsap/SplitText";

import { prefersReducedMotion } from "../utils/variables";

const scopeExcludeSelector = "header, header *, footer, footer *, .library__filters, .library__filters *, .library__tracks, .library__tracks *, .mf-cursor, .mf-cursor *";
const targetSelector = "h1, h2, h3, h4, h5, h6, p, dt, dd, blockquote, figcaption, td, th, label, legend, .large-heading, .large-link, .split-text";
const targetExcludeSelector = `.no-split, .tag, .sr-only, .btn *, ${scopeExcludeSelector}`;
const tagExcludeSelector = `.no-split, ${scopeExcludeSelector}`;
const sectionSelector = "section";
const tagSelector = ".tag";
const lineClass = "split-text-line";

interface SectionGroup {
    section: HTMLElement;
    textEls: HTMLElement[];
    tagEls: HTMLElement[];
}

interface TextState {
    split?: SplitText;
    tween?: gsap.core.Tween;
    isRevealed: boolean;
    delay: number;
}

const textStates = new WeakMap<HTMLElement, TextState>();
const sectionObservers = new WeakMap<HTMLElement, IntersectionObserver>();

export default function initSplitText(container: HTMLElement, delay: number = 0, gate?: Promise<unknown>) {
    if (container.classList.contains("split-text-ready")) return;

    if (prefersReducedMotion) {
        container.classList.add("split-text-ready");
        return;
    }

    const textEls = getTextTargets(container);
    const tagEls = getTagTargets(container);

    if (textEls.length === 0 && tagEls.length === 0) {
        container.classList.add("split-text-ready");
        return;
    }

    hideElements(textEls, tagEls);
    container.classList.add("split-text-ready");

    groupBySection(container, textEls, tagEls).forEach(group => revealOnEnter(group, delay, gate));
}

export function revealText(container: HTMLElement, delay: number = 0) {
    if (prefersReducedMotion) return;

    const textEls = getTextTargets(container);
    const tagEls = getTagTargets(container);

    hideElements(textEls, tagEls);
    revealElements(textEls, tagEls, delay);
}

export function killSplitText(container: HTMLElement) {
    getTextTargets(container).forEach(textEl => {
        const state = textStates.get(textEl);

        state?.split?.kill();
        state?.tween?.kill();
    });

    [container, ...container.querySelectorAll<HTMLElement>(sectionSelector)].forEach(section => {
        sectionObservers.get(section)?.disconnect();
        sectionObservers.delete(section);
    });
}

export function releaseSplitText(textEl: HTMLElement) {
    const state = textStates.get(textEl);
    if (!state) return;

    state.tween?.kill();
    state.split?.revert();
    textStates.delete(textEl);
}

function hideElements(textEls: HTMLElement[], tagEls: HTMLElement[]) {
    textEls.forEach(splitLines);
    if (tagEls.length) gsap.set(tagEls, { opacity: 0, scale: 0.85, "--tag-round": 1, transition: "none" });
}

function getTextTargets(container: HTMLElement): HTMLElement[] {
    const matches = Array.from(container.querySelectorAll<HTMLElement>(targetSelector)).filter(isSplittable);

    return dropNestedMatches(matches);
}

function isSplittable(el: HTMLElement): boolean {
    if (el.closest(targetExcludeSelector)) return false;

    return !!el.textContent?.trim();
}

function dropNestedMatches(elements: HTMLElement[]): HTMLElement[] {
    return elements.filter(el => !elements.some(other => other !== el && other.contains(el)));
}

function getTagTargets(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(tagSelector)).filter(el => !el.closest(tagExcludeSelector));
}

function splitLines(textEl: HTMLElement) {
    textStates.get(textEl)?.split?.revert();
    unwrapClonedLines(textEl);

    const state: TextState = { isRevealed: false, delay: 0 };
    textStates.set(textEl, state);

    state.split = SplitText.create(textEl, {
        type: "lines",
        mask: "lines",
        linesClass: lineClass,
        autoSplit: true,
        onSplit: ({ lines }) => {
            state.tween = gsap.fromTo(lines, { yPercent: 100 }, { yPercent: 0, duration: 0.9, stagger: Math.min(0.04, 0.6 / lines.length), ease: "expo.out", delay: state.delay, paused: !state.isRevealed });

            return state.tween;
        },
    });
}

function unwrapClonedLines(textEl: HTMLElement) {
    textEl.querySelectorAll(`.${lineClass}`).forEach(line => line.parentElement?.replaceWith(...line.childNodes));
}

function groupBySection(container: HTMLElement, textEls: HTMLElement[], tagEls: HTMLElement[]): SectionGroup[] {
    const groups = new Map<HTMLElement, SectionGroup>();

    const getGroup = (el: HTMLElement) => {
        const section = el.closest<HTMLElement>(sectionSelector) ?? container;
        const group = groups.get(section) ?? { section, textEls: [], tagEls: [] };
        groups.set(section, group);

        return group;
    };

    textEls.forEach(el => getGroup(el).textEls.push(el));
    tagEls.forEach(el => getGroup(el).tagEls.push(el));

    return Array.from(groups.values());
}

function revealOnEnter(group: SectionGroup, delay: number, gate?: Promise<unknown>) {
    const observer = new IntersectionObserver(
        entries => {
            if (!entries.some(entry => entry.isIntersecting)) return;

            observer.disconnect();
            void Promise.resolve(gate).then(() => revealElements(group.textEls, group.tagEls, delay));
        },
        { rootMargin: "0px 0px -15% 0px" }
    );

    sectionObservers.get(group.section)?.disconnect();
    sectionObservers.set(group.section, observer);
    observer.observe(group.section);
}

function revealElements(textEls: HTMLElement[], tagEls: HTMLElement[], delay: number) {
    const lineCount = textEls.reduce((count, textEl) => count + (textStates.get(textEl)?.tween?.targets().length ?? 0), 0);
    const lineStagger = Math.min(0.04, 0.6 / lineCount);
    let lineIndex = 0;

    textEls.forEach(textEl => {
        const state = textStates.get(textEl);
        if (!state?.tween) return;

        state.isRevealed = true;
        state.delay = delay + lineIndex * lineStagger;
        state.tween.delay(state.delay).restart(true);
        lineIndex += state.tween.targets().length;
    });

    if (tagEls.length === 0) return;

    gsap.to(tagEls, {
        opacity: 1,
        scale: 1,
        "--tag-round": 0,
        duration: 0.9,
        stagger: Math.min(0.04, 0.6 / tagEls.length),
        ease: "expo.out",
        delay,
        onComplete: () => {
            gsap.set(tagEls, { clearProps: "transition" });
        },
    });
}
